import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button';
import { PdpSharedPurchaseButton } from '../PdpSharedButtons/PdpSharedPurchaseButton';
import { PdpSharedMakeOfferButton } from '../PdpSharedButtons/PdpSharedMakeOfferButton';
import { PdpSharedContactDealerButton } from '../PdpSharedButtons/PdpSharedContactDealerButton';
import { PdpSharedCondensedAuctionButtons } from '../PdpSharedCondensedAuctionButtons/PdpSharedCondensedAuctionButtons';
import { PdpSharedDeposit } from '../PdpSharedDeposit/PdpSharedDeposit';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { FollowSearch } from '../pdpMessages';
import { SaveSearchButton } from '../../components/global/SaveSearchButton/SaveSearchButton';
import { SharedSaveSearchContextProvider } from '../../shared/SharedSaveSearchContext/SharedSaveSearchContext';

// styles
import styles from './PdpSharedCondensedSidebarButtons.scss';

// helpers
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import classnames from 'classnames';

const trackSeeFullDetails = () =>
    trackEvent({
        category: 'product',
        action: 'see full details clicked',
        label: 'landing pdp',
        eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
        interaction_type: interactionTypeConstants.INTERACTION_TYPE_VIEW_MORE_DETAILS,
        step_interaction_name: stepInteractionConstants.STEP_INTERACTION_VIEW_MORE_DETAILS_CLICK,
        trigger: 'pdp',
    });

const handleSeeFullDetails = (pdpUrl, selectedSkuId) => {
    trackSeeFullDetails();
    let modifiedPdpUrl = pdpUrl;
    if (selectedSkuId) {
        modifiedPdpUrl = `${pdpUrl}#skuId=${selectedSkuId}`;
    }
    window.location.assign(handleLocaleUrl(modifiedPdpUrl, GLOBAL_CLIENT_ONLY_LOCALE));
};

const DEFAULT_QUANTITY = '1';

const PdpSharedCondensedSidebarButtonsComponent = ({
    itemSearch,
    relayVariables,
    user,
    item,
    displayInfo,
    selectedSkuId,
}) => {
    const { itemId, userId, fetchAuction } = relayVariables;
    if (!displayInfo || !displayInfo.ctas) {
        return null;
    }

    const { saveSearchData, pdpURL } = item || {};
    const { showRequestPrice, showMakeOffer, showFollowCategory, showPurchase, showAcceptOffer } =
        displayInfo.ctas;
    const followSearchName = saveSearchData?.name;
    const followSearchPage = saveSearchData?.page;

    const inlineClassnames = classnames(styles.inlineButtons, {
        [styles.halfWidth]: showPurchase && showMakeOffer,
    });

    return (
        <div className={styles.buttons} data-tn="item-actions">
            {!showFollowCategory && (
                <Button
                    type="primary"
                    className={styles.seeFullDetails}
                    dataTn="item-actions-view-details"
                    onClick={() => handleSeeFullDetails(pdpURL, selectedSkuId)}
                    size="medium"
                >
                    <FormattedMessage
                        id="PdpShareCondensedSidebarButtons.seeFullDetailsCta"
                        defaultMessage="View More Details"
                    />
                </Button>
            )}
            {fetchAuction ? (
                // Place bid
                <PdpSharedCondensedAuctionButtons item={item} />
            ) : (
                <>
                    <div className={inlineClassnames}>
                        {/* Purchase */}
                        {!!showPurchase && (
                            <div className={styles.purchaseButton}>
                                <PdpSharedPurchaseButton
                                    isPrimary={false}
                                    itemId={itemId}
                                    item={item}
                                    userId={userId}
                                    quantitySelected={DEFAULT_QUANTITY}
                                    size="medium"
                                    skuId={selectedSkuId}
                                    hasPrivateOffer={showAcceptOffer}
                                />
                            </div>
                        )}
                        {/* Make Offer */}
                        {!!showMakeOffer && (
                            <CurrencyProvider
                                render={({ currency }) => (
                                    <div className={styles.makeOfferButton}>
                                        <PdpSharedMakeOfferButton
                                            itemId={itemId}
                                            item={item}
                                            userId={userId}
                                            user={user}
                                            quantitySelected={DEFAULT_QUANTITY}
                                            currency={currency}
                                            showArrow={false}
                                            size="medium"
                                            gaLabel="side rail cta"
                                            hasPrivateOffer={showAcceptOffer}
                                        />
                                    </div>
                                )}
                            />
                        )}
                    </div>
                    {/* Request Price */}
                    {!!showRequestPrice && (
                        <PdpSharedContactDealerButton
                            type={'REQUEST_PRICE'}
                            item={item}
                            user={user}
                            buttonType="secondary"
                            size="medium"
                        />
                    )}
                </>
            )}
            <PdpSharedDeposit userId={userId} item={item} />

            {showFollowCategory && (
                <SharedSaveSearchContextProvider
                    itemSearch={itemSearch}
                    user={user}
                    searchTerm={followSearchName}
                    uriRef={followSearchPage}
                >
                    <p className={styles.followSearchText}>
                        <FollowSearch
                            searchName={followSearchName}
                            searchNameClassname={styles.searchName}
                        />
                    </p>
                    <SaveSearchButton itemSearch={itemSearch} size="medium" type="primary" />
                </SharedSaveSearchContextProvider>
            )}
        </div>
    );
};

PdpSharedCondensedSidebarButtonsComponent.propTypes = {
    relayVariables: PropTypes.object.isRequired,
    viewer: PropTypes.object,
    itemSearch: PropTypes.object,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    userId: PropTypes.string,
    displayInfo: PropTypes.object,
    selectedSkuId: PropTypes.string,
};

export const PdpSharedCondensedSidebarButtons = createFragmentContainer(
    PdpSharedCondensedSidebarButtonsComponent,
    {
        user: graphql`
            fragment PdpSharedCondensedSidebarButtons_user on User {
                ...PdpSharedMakeOfferButton_user
                ...PdpSharedContactDealerButton_user
                ...SharedSaveSearchContext_user
            }
        `,
        item: graphql`
            fragment PdpSharedCondensedSidebarButtons_item on Item {
                ...PdpSharedPurchaseButton_item
                ...PdpSharedContactDealerButton_item
                ...PdpSharedMakeOfferButton_item
                ...PdpSharedDeposit_item
                ...PdpSharedCondensedAuctionButtons_item @include(if: $fetchAuction)
                pdpURL
                saveSearchData {
                    name
                    page
                }
            }
        `,
        itemSearch: graphql`
            fragment PdpSharedCondensedSidebarButtons_itemSearch on ItemSearchQueryConnection {
                ...SaveSearchButton_itemSearch
                ...SharedSaveSearchContext_itemSearch
            }
        `,
        displayInfo: graphql`
            fragment PdpSharedCondensedSidebarButtons_displayInfo on ItemDisplayInfo {
                ctas {
                    showAcceptOffer
                    showPurchase
                    showMakeOffer
                    showFollowCategory
                    showRequestPrice
                }
            }
        `,
    }
);
